/**
 * useTableTracking - хук для интеграции трекинга с Handsontable
 * 
 * ОПТИМИЗАЦИЯ v2:
 * - Убран renderWithTracking() и RenderBatcher
 * - Подсветка через cells callback (Handsontable native)
 * - Нет явных hot.render() вызовов
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type Handsontable from 'handsontable';
import { useUnifiedChangeTracker } from './useUnifiedChangeTracker';
import { getTrackingClassName } from './utils';
import { useTrackingTable } from './TrackingProvider';
import { logger } from '../../../utils/logger';
import type { UseTableTrackingOptions, UseTableTrackingResult, TrackingMode } from './types';

function createAutoTrackingMode(columns: Handsontable.ColumnSettings[]): (row: number, col: number) => boolean {
  return (_row: number, col: number) => {
    if (typeof col !== 'number' || col < 0 || col >= columns.length) return false;
    const column = columns[col];
    if (!column) return false;
    if (column.readOnly === false) return true;
    if (column.readOnly === true) return false;
    return true;
  };
}

function resolveTrackingMode(
  trackingMode: TrackingMode,
  columns: Handsontable.ColumnSettings[]
): (row: number, col: number) => boolean {
  if (typeof trackingMode === 'function') {
    return trackingMode;
  }
  
  switch (trackingMode) {
    case 'auto':
      return createAutoTrackingMode(columns);
    case 'all':
      return () => true;
    case 'none':
      return () => false;
    default:
      return () => true;
  }
}

function djb2Hash(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function computeBaselineSignature(baseline: Array<Array<unknown>> | undefined): string | null {
  if (!baseline || baseline.length === 0) return null;
  let dataString = `${baseline.length}:`;
  for (let i = 0; i < baseline.length; i++) {
    const row = baseline[i];
    if (!row) continue;
    
    for (let j = 0; j < row.length; j++) {
      const value = row[j];
      const normalized = value === null || value === undefined 
        ? '\x00' 
        : String(value);
      dataString += normalized + '\x01';
    }
    dataString += '\x02';
  }
  
  return String(djb2Hash(dataString));
}

export function useTableTracking(options: UseTableTrackingOptions): UseTableTrackingResult {
  const {
    tableId,
    baseline,
    enableTracking,
    enableRowTracking,
    trackingMode,
    derivedCompute,
    columns,
    data,
    rowIds,
    resetOnBaselineChange,
    autoRegisterDirty,
    onTrackingChange,
    onRowOperationsChange,
    hotTableRef,
    userOnAfterChange,
    userAfterRemoveRow,
    userAfterCreateRow,
  } = options;

  const rowIdMappingRef = useRef<Map<number, string | number>>(new Map());
  const tempIdCounterRef = useRef<number>(0);
  const previousBaselineSignatureRef = useRef<string | null>(null);
  const prevChangesCountRef = useRef<number>(0);
  const prevRowOperationsCountRef = useRef<number>(0);

  const { setDirty } = useTrackingTable(tableId);

  // Мемоизируем config — без этого useEffect внутри useUnifiedChangeTracker,
  // зависящие от [config.baseline], запускались бы КАЖДЫЙ рендер (новый объект-литерал).
  const trackerConfig = useMemo(() => ({
    tableId,
    baseline,
  }), [tableId, baseline]);

  const { tracker, changeVersion } = useUnifiedChangeTracker(trackerConfig);

  const finalTrackingMode = useMemo(() => 
    resolveTrackingMode(trackingMode, columns || []),
    [trackingMode, columns]
  );

  // Обновление маппинга rowIds
  useEffect(() => {
    if (enableRowTracking && rowIds) {
      const mapping = new Map<number, string | number>();
      rowIds.forEach((id, index) => {
        mapping.set(index, id);
      });
      rowIdMappingRef.current = mapping;
    }
  }, [enableRowTracking, rowIds]);

  // Вычисление derived changes
  useEffect(() => {
    if (!derivedCompute || !Array.isArray(data)) return;
    
    const dataArray = Array.isArray(data[0]) 
      ? data as unknown[][]
      : columns 
        ? (data as Array<Record<string, unknown>>).map(record => 
            columns.map(col => {
              const accessor = col?.data;
              if (typeof accessor === 'number') return (record as unknown as unknown[])?.[accessor];
              if (typeof accessor === 'string') return record[accessor];
              return null;
            })
          )
        : [];
    
    const derivedSet = derivedCompute(dataArray);
    tracker.updateDerivedChanges(derivedSet);
  }, [derivedCompute, data, columns, tracker]);

  const baselineSignature = useMemo(() => 
    computeBaselineSignature(baseline),
    [baseline]
  );

  // Сброс при изменении baseline
  useEffect(() => {
    if (!enableTracking || !resetOnBaselineChange) return;
    if (!baseline || baselineSignature === previousBaselineSignatureRef.current) return;

    tracker.setBaseline(baseline);
    tracker.reset();
    previousBaselineSignatureRef.current = baselineSignature;
    
    logger.table.baselineReset(tableId, baseline.length);
    
    onTrackingChange?.([]);
    onRowOperationsChange?.([]);
  }, [baselineSignature, enableTracking, resetOnBaselineChange, baseline, tracker, tableId, onTrackingChange, onRowOperationsChange]);

  // Callback для onTrackingChange
  // changeVersion — монотонный счётчик из useUnifiedChangeTracker, растёт при каждом registerChange/updateDerived/etc.
  // Используем его вместо tracker (который теперь стабилен) как trigger для эффектов.
  useEffect(() => {
    if (!enableTracking || !onTrackingChange) return;
    
    const currentCount = tracker.getChangesCount();
    if (currentCount !== prevChangesCountRef.current) {
      const changes = tracker.getChangedCells();
      onTrackingChange(changes);
      prevChangesCountRef.current = currentCount;
    }
  }, [enableTracking, onTrackingChange, tracker, changeVersion]);

  // Callback для onRowOperationsChange
  useEffect(() => {
    if (!enableRowTracking || !onRowOperationsChange) return;
    
    const currentCount = tracker.getAllOperations().length;
    if (currentCount !== prevRowOperationsCountRef.current) {
      const operations = tracker.getAllOperations();
      onRowOperationsChange(operations);
      prevRowOperationsCountRef.current = currentCount;
    }
  }, [enableRowTracking, onRowOperationsChange, tracker, changeVersion]);

  // Обновление dirty count в глобальном провайдере
  useEffect(() => {
    if (!autoRegisterDirty) return;
    const count = tracker.getChangesCount();
    setDirty(count);
  }, [autoRegisterDirty, setDirty, tracker, changeVersion]);

  /**
   * CELLS CALLBACK - главная оптимизация
   * Handsontable вызывает эту функцию для каждой ВИДИМОЙ ячейки при рендере.
   * Возвращаем className для подсветки изменённых ячеек.
   * O(1) проверка через Set.has()
   */
  const cellsCallback = useCallback((row: number, col: number): Handsontable.CellMeta | undefined => {
    if (!enableTracking) return undefined;
    
    // Проверяем изменена ли ячейка пользователем
    if (tracker.isChanged(row, col)) {
      return { className: getTrackingClassName('', 'user') };
    }
    
    // Проверяем является ли ячейка вычисляемой
    if (tracker.isDerived(row, col)) {
      return { className: getTrackingClassName('', 'derived') };
    }
    
    return undefined;
  }, [enableTracking, tracker]);

  // Обработчик afterChange
  const combinedAfterChange = useCallback((
    changes: (Handsontable.CellChange | null)[] | null,
    source: Handsontable.ChangeSource
  ) => {
    if (enableTracking && changes && source !== 'loadData') {
      const hot = hotTableRef.current?.getHotInstance?.();
      
      changes.forEach(change => {
        if (!change) return;
        const [visualRow, prop, , newValue] = change;
        
        const physicalRow = hot ? hot.toPhysicalRow(visualRow) : visualRow;
        
        let colIndex: number | null = null;
        if (typeof prop === 'number') {
          colIndex = prop;
        } else if (typeof prop === 'string' && Array.isArray(columns)) {
          const idx = columns.findIndex((c) => c?.data === prop);
          colIndex = idx >= 0 ? idx : null;
        }
        
        if (typeof colIndex === 'number' && finalTrackingMode(physicalRow, colIndex)) {
          tracker.registerChange(physicalRow, colIndex, newValue, undefined);
        }
      });
    }
    
    userOnAfterChange?.(changes, source);
  }, [enableTracking, tracker, userOnAfterChange, columns, finalTrackingMode, hotTableRef]);

  // Снимок данных удаляемых строк. В afterRemoveRow данные уже удалены из
  // источника — getDataAtRow по тем же индексам вернул бы соседние строки,
  // поэтому захватываем содержимое заранее, в beforeRemoveRow.
  const pendingRemovedRowDataRef = useRef<Map<number, Array<string | number | boolean | null>>>(new Map());

  const combinedBeforeRemoveRow = useCallback<NonNullable<Handsontable.GridSettings['beforeRemoveRow']>>((
    _index,
    _amount,
    physicalRows,
    source
  ) => {
    if (!enableRowTracking || source === 'auto') return;
    const hot = hotTableRef.current?.getHotInstance?.();
    if (!hot) return;

    const snapshot = new Map<number, Array<string | number | boolean | null>>();
    physicalRows.forEach((physicalIndex) => {
      const visualIndex = hot.toVisualRow(physicalIndex);
      if (visualIndex !== null) {
        snapshot.set(physicalIndex, hot.getDataAtRow(visualIndex) as Array<string | number | boolean | null>);
        return;
      }
      // Строка скрыта фильтром — собираем данные из источника
      const sourceRow = hot.getSourceDataAtRow(physicalIndex);
      if (Array.isArray(sourceRow)) {
        snapshot.set(physicalIndex, sourceRow as Array<string | number | boolean | null>);
      } else if (sourceRow && typeof sourceRow === 'object' && Array.isArray(columns)) {
        const record = sourceRow as Record<string, unknown>;
        snapshot.set(physicalIndex, columns.map((col) => {
          const accessor = col?.data;
          return (typeof accessor === 'string' ? record[accessor] : null) as string | number | boolean | null;
        }));
      }
    });
    pendingRemovedRowDataRef.current = snapshot;
  }, [enableRowTracking, columns, hotTableRef]);

  // Обработчик afterRemoveRow
  const combinedAfterRemoveRow = useCallback<NonNullable<Handsontable.GridSettings['afterRemoveRow']>>((
    index,
    amount,
    physicalRows,
    source
  ) => {
    const capturedRows = pendingRemovedRowDataRef.current;
    pendingRemovedRowDataRef.current = new Map();

    if (enableRowTracking && source !== 'auto') {
      physicalRows.forEach((physicalIndex) => {
        const rowId = rowIdMappingRef.current.get(physicalIndex) ?? rowIds?.[physicalIndex];
        if (rowId !== undefined) {
          tracker.registerRowOperation('deleted', physicalIndex, rowId, capturedRows.get(physicalIndex));
          logger.tracking.rowDeleted(tableId, physicalIndex, rowId);
        }
      });
    }

    if (enableTracking || enableRowTracking) {
      // Источник данных сплайснут — физические индексы строк ниже удалённых
      // сдвинулись. Сдвигаем ключи изменений, baseline и маппинг rowId.
      tracker.shiftRowsForRemoval(physicalRows);

      if (rowIdMappingRef.current.size > 0) {
        const sortedDesc = [...physicalRows].sort((a, b) => b - a);
        for (const removed of sortedDesc) {
          const next = new Map<number, string | number>();
          rowIdMappingRef.current.forEach((id, idx) => {
            if (idx === removed) return;
            next.set(idx > removed ? idx - 1 : idx, id);
          });
          rowIdMappingRef.current = next;
        }
      }
    }

    userAfterRemoveRow?.(index, amount, physicalRows, source);
  }, [enableTracking, enableRowTracking, tracker, rowIds, userAfterRemoveRow, tableId]);

  // Обработчик afterCreateRow
  const combinedAfterCreateRow = useCallback<NonNullable<Handsontable.GridSettings['afterCreateRow']>>((
    index,
    amount,
    source
  ) => {
    const isUserAction = source !== 'auto' && source !== 'loadData';
    if ((enableTracking || enableRowTracking) && isUserAction) {
      const hot = hotTableRef.current?.getHotInstance?.();
      if (hot) {
        const basePhysical = hot.toPhysicalRow(index);
        if (typeof basePhysical === 'number' && basePhysical >= 0) {
          // Сначала сдвигаем ключи существующих строк, затем регистрируем новые
          tracker.shiftRowsForInsertion(basePhysical, amount);

          if (rowIdMappingRef.current.size > 0) {
            const next = new Map<number, string | number>();
            rowIdMappingRef.current.forEach((id, idx) => {
              next.set(idx >= basePhysical ? idx + amount : idx, id);
            });
            rowIdMappingRef.current = next;
          }

          if (enableRowTracking) {
            for (let i = 0; i < amount; i++) {
              const visualIndex = index + i;
              const physicalIndex = hot.toPhysicalRow(visualIndex);
              const rowData = hot.getDataAtRow(visualIndex) as Array<string | number | boolean | null>;
              const tempId = `temp_${tempIdCounterRef.current++}`;
              tracker.registerRowOperation('added', physicalIndex, tempId, rowData || []);
              rowIdMappingRef.current.set(physicalIndex, tempId);
              logger.tracking.rowAdded(tableId, physicalIndex, tempId);
            }
          }
        }
      }
    }

    userAfterCreateRow?.(index, amount, source);
  }, [enableTracking, enableRowTracking, tracker, userAfterCreateRow, tableId, hotTableRef]);

  // Восстановление baseline значений
  const restoreBaselineValues = useCallback(() => {
    const hot = hotTableRef.current?.getHotInstance?.();
    if (!hot) return;
    
    const changedCells = tracker.getChangedCells();
    const baselineData = tracker.getBaseline();

    if (changedCells.length > 0 && baselineData) {
      // change.row — ФИЗИЧЕСКИЙ индекс (так регистрирует combinedAfterChange),
      // а setDataAtCell ждёт ВИЗУАЛЬНЫЙ. При активной сортировке без конвертации
      // откат записал бы baseline-значения в чужие строки.
      const updates: [number, number, string | number | boolean | null][] = [];

      hot.batch(() => {
        changedCells.forEach(change => {
          const baselineValue = baselineData[change.row]?.[change.col];
          if (baselineValue === undefined) return;

          const visualRow = hot.toVisualRow(change.row);
          if (visualRow === null) {
            // Строка скрыта фильтром — у неё нет визуального индекса, пишем в источник
            const accessor = Array.isArray(columns) ? columns[change.col]?.data : undefined;
            const sourceCol = (typeof accessor === 'string' || typeof accessor === 'number')
              ? accessor
              : change.col;
            hot.setSourceDataAtCell(change.row, sourceCol, baselineValue, 'restoreBaseline');
          } else {
            updates.push([visualRow, change.col, baselineValue]);
          }
        });

        if (updates.length > 0) {
          hot.setDataAtCell(updates, 'restoreBaseline');
        }
      });
    }
    
    tracker.clearAllChanges();

    // Force Handsontable to re-render after clearing tracking.
    // The batch render above may have occurred before clearAllChanges(),
    // so some cells might still display stale tracking classes.
    const hotAfter = hotTableRef.current?.getHotInstance?.();
    if (hotAfter) {
      hotAfter.render();
    }
  }, [tracker, hotTableRef, columns]);

  return {
    tracker,
    changeVersion,
    combinedAfterChange,
    combinedAfterRemoveRow,
    combinedAfterCreateRow,
    combinedBeforeRemoveRow,
    restoreBaselineValues,
    cellsCallback,
    rowIdMappingRef,
  };
}
